import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { toast } from "sonner";
import { Eye, CheckCircle2, XCircle, Trash2, MoreVertical, GraduationCap } from "lucide-react";

export default function PendingStudentsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [viewDetail, setViewDetail] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // {kind, student}

  const canApprove = hasPerm(user, "pending_student", "approve");
  const canReject = hasPerm(user, "pending_student", "reject");
  const canDelete = hasPerm(user, "student_list", "delete");

  const load = () => api.get("/users/pending").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openView = async (s) => {
    setViewing(s); setViewLoading(true); setViewDetail(null);
    try {
      const r = await api.get(`/users/students/${s.id}`);
      setViewDetail(r.data);
    } catch (e) {
      toast.error("Could not load student details");
    } finally {
      setViewLoading(false);
    }
  };

  const runConfirmed = async () => {
    if (!confirm) return;
    const { kind, student } = confirm;
    try {
      if (kind === "approve") {
        await api.post(`/users/${student.id}/approve`);
        toast.success("Student has been approved and enrolled successfully.");
      } else if (kind === "reject") {
        await api.post(`/users/${student.id}/reject`);
        toast.success("Student application has been rejected.");
      } else if (kind === "delete") {
        await api.delete(`/users/${student.id}`);
        toast.success("Student has been deleted successfully.");
      }
      setConfirm(null);
      setViewing(null);
      setViewDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const confirmCfg = {
    approve: { title: "Approve student?", desc: "The student will gain access to their enrolled course.", cta: "Approve", ctaClass: "bg-green-600 hover:bg-green-700" },
    reject:  { title: "Reject student?", desc: "The student will not be able to access the platform.", cta: "Reject", ctaClass: "bg-red-600 hover:bg-red-700" },
    delete:  { title: "Permanently delete this student?", desc: "This action cannot be undone. If the student has any data, the account will be archived (soft-deleted).", cta: "Delete", ctaClass: "bg-red-600 hover:bg-red-700" },
  };
  const cm = confirm ? confirmCfg[confirm.kind] : null;

  const Actions = ({ s, mobile = false }) => {
    const items = [
      { key: "view", label: "View", icon: Eye, onClick: () => openView(s), show: true },
      { key: "approve", label: "Approve", icon: CheckCircle2, onClick: () => setConfirm({ kind: "approve", student: s }), show: canApprove, className: "text-green-700" },
      { key: "reject", label: "Reject", icon: XCircle, onClick: () => setConfirm({ kind: "reject", student: s }), show: canReject, className: "text-red-600" },
      { key: "delete", label: "Delete", icon: Trash2, onClick: () => setConfirm({ kind: "delete", student: s }), show: canDelete, className: "text-red-600", danger: true },
    ].filter((i) => i.show);

    if (mobile) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="outline" data-testid={`pending-menu-${s.id}`}><MoreVertical className="w-4 h-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            {items.map((it, idx) => (
              <div key={it.key}>
                {it.danger && idx > 0 && <DropdownMenuSeparator />}
                <DropdownMenuItem className={it.className} onClick={it.onClick}><it.icon className="w-4 h-4 mr-2" />{it.label}</DropdownMenuItem>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }
    return (
      <div className="flex items-center justify-end gap-1">
        {items.map((it) => (
          <Button key={it.key} size="icon" variant="outline" className={it.className} onClick={it.onClick} title={it.label} data-testid={`pending-${it.key}-${s.id}`}>
            <it.icon className="w-4 h-4" />
          </Button>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="pending-students-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Approvals</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Pending Students</h1>
        <p className="text-slate-600 mt-1">Review and approve new student registrations.</p>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden grid gap-3">
        {items.length === 0 ? <Card className="p-8 text-center text-slate-500">No pending students</Card> :
          items.map((u) => (
            <Card key={u.id} className="p-4 border-slate-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{u.full_name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Pending</Badge>
                    {u.selected_course_name && <Badge variant="outline" className="font-normal"><GraduationCap className="w-3 h-3 mr-1" />{u.selected_course_name}</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 mt-2">{u.contact_number || "—"} • {u.school || "—"}</div>
                </div>
                <Actions s={u} mobile />
              </div>
            </Card>
          ))}
      </div>

      {/* Desktop table */}
      <Card className="p-0 overflow-hidden border-slate-200 hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>School</TableHead>
              <TableHead>Selected Course</TableHead>
              <TableHead>Registered</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">No pending students</TableCell></TableRow> :
              items.map((u) => (
                <TableRow key={u.id} className="even:bg-slate-50">
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.contact_number || "—"}</TableCell>
                  <TableCell>{u.school || "—"}</TableCell>
                  <TableCell>{u.selected_course_name ? <Badge variant="outline" className="font-normal"><GraduationCap className="w-3 h-3 mr-1" />{u.selected_course_name}</Badge> : <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">{u.created_at?.slice(0, 10)}</TableCell>
                  <TableCell><Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Pending</Badge></TableCell>
                  <TableCell><Actions s={u} /></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      {/* View Student Modal */}
      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) { setViewing(null); setViewDetail(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Student Application Details</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-display text-lg font-bold">
                  {(viewing.full_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-xl truncate">{viewing.full_name}</div>
                  <div className="text-sm text-slate-500 truncate">{viewing.email}</div>
                </div>
                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Pending</Badge>
              </div>

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div><div className="text-xs text-slate-500">Contact Number</div><div className="font-medium">{viewing.contact_number || "—"}</div></div>
                <div><div className="text-xs text-slate-500">School</div><div className="font-medium">{viewing.school || "—"}</div></div>
                <div className="sm:col-span-2"><div className="text-xs text-slate-500">Selected Course to Enroll</div><div className="font-medium text-blue-700">{viewing.selected_course_name || "—"}</div></div>
                <div><div className="text-xs text-slate-500">Address</div><div className="font-medium">{viewing.address || "—"}</div></div>
                <div><div className="text-xs text-slate-500">Year Level</div><div className="font-medium">{viewing.year_level || "—"}</div></div>
                <div className="sm:col-span-2"><div className="text-xs text-slate-500">Board Exam Category</div><div className="font-medium">{viewing.board_exam_category || "—"}</div></div>
                <div><div className="text-xs text-slate-500">Registration Date</div><div className="font-medium">{viewing.created_at?.slice(0, 16).replace("T", " ")}</div></div>
                <div><div className="text-xs text-slate-500">Last Login</div><div className="font-medium">{viewing.last_login?.slice(0, 16).replace("T", " ") || "—"}</div></div>
              </div>

              {viewLoading && <div className="text-sm text-slate-500 text-center">Loading…</div>}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setViewing(null)} data-testid="pending-close-btn">Close</Button>
            {canReject && viewing && <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirm({ kind: "reject", student: viewing })} data-testid="pending-reject-modal-btn">Reject Student</Button>}
            {canApprove && viewing && <Button className="bg-green-600 hover:bg-green-700" onClick={() => setConfirm({ kind: "approve", student: viewing })} data-testid="pending-approve-modal-btn">Approve Student</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation */}
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{cm?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {cm?.desc}
              {confirm?.student && (<span className="block mt-2 font-medium text-slate-900">{confirm.student.full_name} — {confirm.student.email}</span>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirmed} className={cm?.ctaClass}>{cm?.cta}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
